import { Box, Button, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import CountdownClock from "../CountdownClock";
import { useCustomColors } from "../../hooks/useCustomColors";
import { heroSurfaceSx } from "./heroSurface";
import type { CurrentRound } from "../../api";
import rabbitMascot from "../../assets/rabbit-win-2.png";

/** Splits a line at its first digit, so "Every 5 Minutes." foils only "5 Minutes.". */
const splitAtNumber = (line: string) => {
  const at = line.search(/\d/);
  return at < 0 ? [line, ""] : [line.slice(0, at), line.slice(at)];
};

interface LottoHeroProps {
  round: CurrentRound | null;
  remaining: number;
  isOpen: boolean;
  onPlay: () => void;
}

const LottoHero = ({
  round,
  remaining,
  isOpen,
  onPlay,
}: LottoHeroProps) => {
  const colors = useCustomColors();
  const { t } = useTranslation();
  const eyebrowSx = {
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "3px",
    color: colors.text.secondary,
  };

  return (
    <Box
      sx={{
        ...heroSurfaceSx(colors),
        mt: 0,
        minHeight: { xs: "420px", md: "380px" },
        p: { xs: "24px 20px 0", md: "44px 48px" },
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(320px, 0.8fr)" },
        alignItems: "center",
        gap: { xs: "20px", md: "28px" },
      }}
    >
      <Box sx={{ position: "relative", zIndex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" sx={{ gap: "8px" }}>
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              backgroundColor: colors.accent.green,
              boxShadow: `0 0 12px ${colors.accent.green}`,
            }}
          />
          <Typography sx={{ ...eyebrowSx, color: colors.accent.green }}>
            {t("dashboard_hero_badge")}
          </Typography>
        </Stack>
        <Typography
          sx={{
            mt: "16px",
            fontFamily: "Montserrat, sans-serif",
            fontSize: { xs: "30px", md: "48px" },
            fontWeight: 800,
            fontStyle: "italic",
            lineHeight: 1.18,
            color: colors.text.primary,
          }}
        >
          {/* Only the interval itself is foiled; every other word is left alone. */}
          {t("dashboard_hero_title").split("\n").map((line, index) => {
            const [before, interval] = index === 0 ? splitAtNumber(line) : [line, ""];
            return (
              <span key={line}>
                {before}
                {interval && <span className="fx-foil">{interval}</span>}
                <br />
              </span>
            );
          })}
        </Typography>
        <Typography
          sx={{
            mt: "16px",
            fontSize: { xs: "13px", md: "14px" },
            lineHeight: 1.9,
            color: colors.text.secondary,
          }}
        >
          {t("dashboard_hero_description").split("\n").map((line) => (
            <span key={line}>
              {line}
              <br />
            </span>
          ))}
        </Typography>

        <Stack direction="row" flexWrap="wrap" sx={{ gap: { xs: "16px", md: "32px" }, mt: "22px" }}>
          <Box>
            <Typography sx={eyebrowSx}>{t("dashboard_current_period")}</Typography>
            <Typography sx={{ mt: "4px", fontSize: "18px", fontWeight: 800, color: colors.text.primary }}>
              {round?.period_id ?? "—"}
            </Typography>
          </Box>
          <Box>
            <Typography sx={eyebrowSx}>{t("dashboard_next_draw")}</Typography>
            <CountdownClock
              seconds={round ? remaining : 0}
              sx={{
                mt: "4px",
                fontSize: "18px",
                fontWeight: 800,
                color: isOpen ? colors.accent.gold : colors.accent.amber,
              }}
            />
          </Box>
        </Stack>

        <Stack direction="row" flexWrap="wrap" sx={{ gap: "12px", mt: "26px" }}>
          <Button onClick={onPlay} sx={{ height: "50px", borderRadius: "12px", px: "28px" }}>
            {t("dashboard_play_now")}
          </Button>
        </Stack>
      </Box>

      <Box
        component="img"
        src={rabbitMascot}
        alt=""
        aria-hidden="true"
        sx={{
          width: "100%",
          maxWidth: { xs: "270px", md: "440px" },
          height: "auto",
          maxHeight: { md: "350px" },
          objectFit: "contain",
          justifySelf: { xs: "center", md: "end" },
          alignSelf: "end",
          position: "relative",
          zIndex: 1,
          filter: "drop-shadow(0 18px 28px rgba(0, 0, 0, 0.38))",
        }}
      />
    </Box>
  );
};

export default LottoHero;
